import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { del } from 'idb-keyval';
import { authAPI, workspaceAPI, billingAPI } from '../services/api';
import { clearWhatsAppDrafts } from '../utils/whatsappDrafts';

const AuthContext = createContext(null);

// Read cached user from localStorage synchronously (avoids flash)
function getCachedUser() {
  try {
    const raw = localStorage.getItem('user');
    if (raw) return JSON.parse(raw);
  } catch {
    // corrupted — ignore
  }
  return null;
}

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();

  // If we have a token + cached user, start with it immediately — no loading spinner
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const [user, setUser] = useState(() => (token ? getCachedUser() : null));
  const [loading, setLoading] = useState(!user);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          // Fetch fresh data in background — user already sees cached version
          const { data } = await authAPI.getMe();
          let workspace = null;
          try {
            const workspaceRes = await workspaceAPI.getMe();
            workspace = workspaceRes?.data || null;
          } catch (err) {
            workspace = null;
          }
          let billing = null;
          try {
            const billingRes = await billingAPI.getMe();
            billing = billingRes?.data || null;
          } catch (err) {
            billing = null;
          }
          const previousUser = getCachedUser();
          if (previousUser?.workspaceOwnerId !== workspace?.workspaceOwnerId) {
            if (workspace?.workspaceOwnerId && (previousUser?.workspaceOwnerId || previousUser?.id) !== workspace.workspaceOwnerId) clearWhatsAppDrafts();
            queryClient.clear();
            await del('leadmarka-query-cache');
          }
          const mergedUser = { ...data, ...(workspace || {}), subscription: billing };
          localStorage.setItem('user', JSON.stringify(mergedUser));
          setUser(mergedUser);
        } catch (error) {
          // A lost connection is not a rejected login. Keep the saved session
          // available offline, and verify it again when connectivity returns.
          if (error.response?.status === 401 || error.response?.status === 403) {
            clearWhatsAppDrafts();
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            queryClient.clear();
            del('leadmarka-query-cache').catch(() => {});
            setUser(null);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
    window.addEventListener('online', initAuth);
    window.addEventListener('focus', initAuth);
    return () => { window.removeEventListener('online', initAuth); window.removeEventListener('focus', initAuth); };
  }, [queryClient]);

  const login = async (email, password) => {
    const { data } = await authAPI.login(email, password);
    clearWhatsAppDrafts();
    localStorage.setItem('token', data.token);
    let workspace = null;
    try {
      const workspaceRes = await workspaceAPI.getMe();
      workspace = workspaceRes?.data || null;
    } catch (err) {
      workspace = null;
    }
    let billing = null;
    try {
      const billingRes = await billingAPI.getMe();
      billing = billingRes?.data || null;
    } catch (err) {
      billing = null;
    }
    const mergedUser = { ...data.user, ...(workspace || {}), subscription: billing };
    localStorage.setItem('user', JSON.stringify(mergedUser));
    setUser(mergedUser);
    return data;
  };

  const register = async (userData) => {
    const { data } = await authAPI.register(userData);
    clearWhatsAppDrafts();
    localStorage.setItem('token', data.token);
    let workspace = null;
    try {
      const workspaceRes = await workspaceAPI.getMe();
      workspace = workspaceRes?.data || null;
    } catch (err) {
      workspace = null;
    }
    let billing = null;
    try {
      const billingRes = await billingAPI.getMe();
      billing = billingRes?.data || null;
    } catch (err) {
      billing = null;
    }
    const mergedUser = { ...data.user, ...(workspace || {}), subscription: billing };
    localStorage.setItem('user', JSON.stringify(mergedUser));
    setUser(mergedUser);
    return data;
  };

  const logout = useCallback(() => {
    clearWhatsAppDrafts();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear in-memory React Query cache
    queryClient.clear();
    // Clear persisted IndexedDB query cache to prevent data leaking to next user
    del('leadmarka-query-cache').catch(() => {});
    setUser(null);
  }, [queryClient]);

  const updateUser = async (updates) => {
    const { data } = await authAPI.updateProfile(updates);
    const updatedUser = { ...user, ...data };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const updateWorkspaceSettings = async (updates) => {
    const { data } = await workspaceAPI.updateSettings(updates);
    const updatedUser = { ...user, ...data };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const userId = user?.id;
  const workspaceOwnerId = user?.workspaceOwnerId;
  const refreshSubscription = useCallback(async () => {
    const sessionToken = localStorage.getItem('token');
    const save = subscription => {
      if (localStorage.getItem('token') !== sessionToken) return;
      const saved = getCachedUser();
      if (saved?.id !== userId || saved?.workspaceOwnerId !== workspaceOwnerId) return;
      setUser(current => {
        if (!current || current.id !== userId || current.workspaceOwnerId !== workspaceOwnerId) return current;
        const updated = { ...current, subscription };
        localStorage.setItem('user', JSON.stringify(updated));
        return updated;
      });
      if (saved?.subscription?.planCode !== subscription?.planCode || saved?.subscription?.status !== subscription?.status) {
        queryClient.invalidateQueries();
      }
    };
    try {
      const { data } = await billingAPI.getMe();
      save(data);
      return data;
    } catch (error) {
      save(null);
      throw error;
    }
  }, [userId, workspaceOwnerId, queryClient]);

  const value = {
    user,
    login,
    register,
    logout,
    updateUser,
    updateWorkspaceSettings,
    refreshSubscription,
    isAuthenticated: !!user,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
